/** Pipeline Activity tab — action-driven view.
 *
 * Cards + helpers live in PipelineActivityCards.tsx so this stays
 * under the 200 LOC cap.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Inbox, AlertCircle, ListChecks } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import { FEATURE_ESCALATIONS } from '@/lib/features';
import type { EscalationTask } from '@/pages/escalations/types';
import { PausedJobCard, EscalationCard } from './PipelineActivityCards';
import type { PausedJob } from './activityHelpers';

interface PipelineActivityResponse {
  items_waiting_on_me: number;
  my_open_escalations: number;
  paused_total: number;
  paused_jobs: PausedJob[];
}

interface Props {
  onCountChange?: (n: number | null) => void;
}

export function PipelineActivityTab({ onCountChange }: Props) {
  const navigate = useNavigate();
  const [activity, setActivity] = useState<PipelineActivityResponse | null>(null);
  const [escalations, setEscalations] = useState<EscalationTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const escalationsRequest = FEATURE_ESCALATIONS
      ? api
        .get<{ data: EscalationTask[] }>('/api/v1/escalations?status=open')
        .catch(() => ({ data: [] as EscalationTask[] }))
      : Promise.resolve({ data: [] as EscalationTask[] });
    Promise.all([
      api.get<PipelineActivityResponse>('/api/v1/dashboard/pipeline-activity'),
      escalationsRequest,
    ])
      .then(([a, e]) => {
        setActivity(a);
        setEscalations(e.data);
        onCountChange?.(a.items_waiting_on_me);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Load failed.');
        onCountChange?.(null);
      })
      .finally(() => { setLoading(false); });
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  if (loading && !activity) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading pipeline activity…
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button variant="ghost" size="sm" onClick={load}>Retry</Button>
        </AlertDescription>
      </Alert>
    );
  }
  if (!activity) return null;

  return (
    <div className="space-y-4">
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
        <CardContent className="pt-4 pb-4 px-4 flex items-center gap-3">
          <Inbox className="h-8 w-8 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-900 dark:text-amber-200">
              Items waiting on you
            </p>
            <p className="text-3xl font-bold text-amber-900 dark:text-amber-100">
              {activity.items_waiting_on_me}
            </p>
            <p className="text-xs text-amber-900/70 dark:text-amber-200/70 mt-1">
              {activity.paused_total} unfinished pipeline
              {activity.paused_total === 1 ? '' : 's'}
              {FEATURE_ESCALATIONS && (
                <>
                  {' · '}
                  {activity.my_open_escalations} open escalation
                  {activity.my_open_escalations === 1 ? '' : 's'}
                </>
              )}
            </p>
          </div>
          {FEATURE_ESCALATIONS && activity.my_open_escalations > 0 && (
            <Button onClick={() => { navigate('/escalations'); }} variant="outline" size="sm">
              View escalations
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Unfinished pipelines ({activity.paused_jobs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {activity.paused_jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3">
              Nothing waiting. New submissions land here as they pause for review.
            </p>
          ) : (
            activity.paused_jobs.map((j) => <PausedJobCard key={j.id} job={j} />)
          )}
        </CardContent>
      </Card>

      {FEATURE_ESCALATIONS && escalations.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Inbox className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              My open escalations ({escalations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {escalations.map((t) => <EscalationCard key={t.id} t={t} />)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
